import { signal } from "@preact/signals";
import { getDeviceId, setDeviceToken } from "./config.js";
import { relayBase } from "./store.js";

/**
 * Geräte-Registrierung: scannt ein Firmen-Handy den Betreiber-QR (`?enroll=<token>`),
 * meldet die PWA ihre stabile deviceId an die Cloud (enroll.php) → das Gerät kommt ins
 * Inventar (typ "handy"). KEINE Veranstaltungs-Zuordnung hier (passt zum Inventar-Modell).
 */

/** Enroll-Token aus der URL (`?enroll=`), oder null. Bestimmt, ob der Enroll-Modus greift. */
export const enrollToken = new URLSearchParams(location.search).get("enroll");

export const enrollState = signal<"working" | "done" | "error">("working");
export const enrollMsg = signal("");
export const enrollInvNr = signal<number | null>(null);

export async function runEnroll(): Promise<void> {
  if (!enrollToken) return;
  enrollState.value = "working";
  enrollMsg.value = "";
  try {
    // venue aus dem QR (?enroll=…&venue=…) mitschicken → Gerät wird gleich der Veranstaltung
    // zugewiesen. NUR aus dem URL-Parameter (nicht der gecachten/Dev-Venue), damit ein QR ohne
    // venue reines Inventar-Enroll bleibt statt versehentlich der Dev-Venue zuzuweisen.
    const venueParam = new URLSearchParams(location.search).get("venue");
    // Herkunft aus dem QR (?kind=betreiber|byd): Betreiber-Handy (Verwaltung/Geräte, LAN) vs.
    // BYD-Handy (Venue/Kellner-App, Mobilfunk). Bestimmt owner_class im Inventar.
    const kindParam = new URLSearchParams(location.search).get("kind");
    const ownerClass = kindParam === "betreiber" || kindParam === "byd" ? kindParam : undefined;
    const body = JSON.stringify({
      deviceId: getDeviceId(),
      ...(venueParam ? { venueId: venueParam } : {}),
      ...(ownerClass ? { ownerClass } : {}),
    });

    // Enroll-Ziel: im LAN wird der App-Origin (kasse-<host>) auf den lokalen Server aufgelöst,
    // der /enroll.php zur Cloud proxyt — das Kellner-Handy hat aber KEIN Internet (totes Gateway),
    // erreicht `relayBase()` (die öffentliche Cloud) also nicht. Darum zuerst same-origin (lokaler
    // Server) versuchen, dann relayBase() (reiner Cloud-/Mobilfunk-Betrieb, wo der kasse-vhost nur
    // statisch ist und die PHP-Endpunkte unter <host> liegen). Erste echte Enroll-Antwort gewinnt.
    const bases = [location.origin, relayBase()].filter((b, i, a) => a.indexOf(b) === i);
    let lastErr = "";
    for (const base of bases) {
      let j: { ok?: boolean; inventarNr?: number; deviceToken?: string | null; error?: string };
      try {
        const res = await fetch(`${base}/enroll.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Pi-Token": enrollToken },
          body,
        });
        j = await res.json();
      } catch {
        // Netzwerkfehler oder kein JSON (z. B. statischer kasse-vhost liefert HTML/404) → nächste Basis.
        lastErr = "nicht erreichbar";
        continue;
      }
      // Nur eine echte Enroll-Antwort (hat ok ODER error) ist definitiv — sonst nächste Basis.
      if (j.ok === undefined && j.error === undefined) { lastErr = "unerwartete Antwort"; continue; }
      if (!j.ok) throw new Error(j.error || "Registrierung abgelehnt");
      // Schreib-Token merken (Klartext kommt nur hier einmalig) → damit darf das Gerät später
      // über die Cloud bestellen. Ohne ihn bliebe der Cloud-Push mangels Auth erfolglos.
      if (j.deviceToken) setDeviceToken(j.deviceToken);
      enrollInvNr.value = j.inventarNr ?? null;
      enrollState.value = "done";
      return;
    }
    throw new Error(lastErr || "Server nicht erreichbar");
  } catch (e) {
    enrollMsg.value = e instanceof Error ? e.message : String(e);
    enrollState.value = "error";
  }
}
